import { useEffect } from 'react'
import { readTokenIssuedLifetime, wouldSilentRenewStorm } from './auth-recovery'

/**
 * Minimum surface the storm guard needs out of `react-oidc-context`'s
 * `useAuth()` return value. Spelled out here so the hook is callable
 * from tests with a hand-rolled mock instead of dragging the full
 * `AuthContextProps` shape into the test bed.
 */
export interface SilentRenewStormGuardAuth {
  user?: { access_token?: string } | null
  // Function-type field, not method shorthand: encodes
  // `this: void` so destructuring the method off the wrapper is safe
  // (no `@typescript-eslint/unbound-method` complaint). The upstream
  // `react-oidc-context` binds these methods to the UserManager
  // instance, so detached invocation is sound at runtime too.
  stopSilentRenew: () => void
}

/**
 * Pre-empt the `oidc-client-ts` silent-renew storm. When a freshly-
 * issued OIDC token's lifetime is at or below the storm floor, stop
 * the lib's silent-renew timer; the next RPC 401 then routes through
 * the existing recovery path.
 *
 * Keyed on `access_token` plus `stopSilentRenew`, not the whole
 * `auth` wrapper. Two reasons:
 *
 *  - `expires_at` won't move in Keycloak's clamped regime — successive
 *    refreshes share `exp = auth_time + ssoSessionMax` (a fixed
 *    boundary) while only `iat` advances. The JWT itself is the only
 *    signal that reliably rotates per issuance.
 *  - The whole `auth` object rotates on unrelated state changes from
 *    `react-oidc-context` (isLoading, error, events). Including it in
 *    deps would re-fire the effect spuriously and call
 *    `stopSilentRenew` again inside the storm window. Pinning the
 *    deps to the two actual inputs the effect uses keeps the contract
 *    honest.
 */
export function useSilentRenewStormGuard(
  auth: SilentRenewStormGuardAuth,
): void {
  const accessToken = auth.user?.access_token
  const { stopSilentRenew } = auth
  useEffect(() => {
    const issuedLifetime = readTokenIssuedLifetime(accessToken)
    if (!wouldSilentRenewStorm(issuedLifetime)) return
    try { stopSilentRenew() } catch { /* idempotent — ignore */ }
  }, [stopSilentRenew, accessToken])
}
